import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { RevenueCycleDto, UnclaimedInvoiceDto } from '@his/shared';
import { downloadGet, get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { printDocument } from '@/lib/print';
import { dateOnly, humanize, money } from '@/lib/utils';
import { tr } from '@/lib/i18n-core';
import { DataTable, Dialog, FilterField, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Alert, Button, DatePicker, Field, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

const monthStart = () => `${new Date().toISOString().slice(0, 8)}01`;
const today = () => new Date().toISOString().slice(0, 10);

function PayerSelect({ value, onChange, allowAll }: { value: string; onChange: (value: string) => void; allowAll?: boolean }) {
  const payers = useQuery({ queryKey: ['payers'], queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers') });
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{allowAll ? 'Every payer' : 'Choose…'}</option>
      {payers.data?.map((payer) => (
        <option key={payer.id} value={payer.id}>
          {payer.name}
        </option>
      ))}
    </Select>
  );
}

export function UnclaimedList() {
  const [payerId, setPayerId] = useState('');
  const list = useQuery({
    queryKey: ['unclaimed', payerId],
    queryFn: () => get<{ items: UnclaimedInvoiceDto[] }>(`/insurance/claims/unclaimed${qs({ payerId: payerId || undefined, limit: 50 })}`),
  });
  return (
    <div>
      <p className="mb-2 text-sm font-medium">Bills not on any claim yet, oldest first</p>
      <SearchFilterBar
        filters={
          <FilterField label={tr('billing', 'payer')} className="w-56">
            <PayerSelect value={payerId} onChange={setPayerId} allowAll />
          </FilterField>
        }
        onReset={payerId ? () => setPayerId('') : undefined}
      />
      <DataTable
        dense
        rows={list.data?.items}
        loading={list.isLoading} refreshing={list.isFetching}
        error={list.error}
        rowKey={(row) => row.invoiceId}
        empty="Every panel bill is on a claim."
        columns={[
          { header: 'Bill', sort: (row) => row.number, cell: (row) => <span className="tabular text-xs">{row.number}</span> },
          { header: 'Patient', sort: (row) => row.patientName, cell: (row) => row.patientName ?? '—' },
          { header: 'Payer', sort: (row) => row.payerName, cell: (row) => row.payerName ?? '—' },
          { header: 'Panel share', align: 'right', sort: (row) => row.panelMinor, cell: (row) => money(row.panelMinor) },
          { header: 'Age', align: 'right', sort: (row) => row.ageDays, cell: (row) => `${row.ageDays} d` },
          { header: 'Submit by', sort: (row) => row.submitBy, cell: (row) => (row.submitBy ? <span className={row.overdue ? 'font-semibold text-destructive' : ''}>{dateOnly(row.submitBy)}</span> : '—') },
        ]}
      />
    </div>
  );
}

export function ClaimExportButton() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await downloadGet(`/insurance/claims/export${qs({ from: monthStart(), to: today() })}`, `claims-${today()}.csv`);
        } catch (caught) {
          toast.error('Could not export', describeError(caught));
        } finally {
          setBusy(false);
        }
      }}
    >
      Export this month (CSV)
    </Button>
  );
}

export function RevenueCycleTab() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [payerId, setPayerId] = useState('');
  const report = useQuery({
    queryKey: ['revenue-cycle', from, to, payerId],
    queryFn: () => get<RevenueCycleDto>(`/insurance/reports/revenue-cycle${qs({ from, to, payerId: payerId || undefined })}`),
    enabled: Boolean(from && to),
  });
  const data = report.data;
  const changed = from !== monthStart() || to !== today() || payerId;
  return (
    <div className="space-y-4">
      <SearchFilterBar
        className="mb-0"
        filters={
          <>
            <FilterField label={tr('billing', 'from')}>
              <DatePicker value={from} onChange={(value) => setFrom(value)} className="w-40" />
            </FilterField>
            <FilterField label={tr('billing', 'to_2')}>
              <DatePicker value={to} onChange={(value) => setTo(value)} className="w-40" />
            </FilterField>
            <FilterField label={tr('billing', 'payer')} className="w-56">
              <PayerSelect value={payerId} onChange={setPayerId} allowAll />
            </FilterField>
          </>
        }
        onReset={
          changed
            ? () => {
                setFrom(monthStart());
                setTo(today());
                setPayerId('');
              }
            : undefined
        }
      />
      {report.error && <Alert variant="destructive">{describeError(report.error)}</Alert>}
      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Claims submitted" value={data.claimsSubmitted} />
            <StatTile label="Clean claim rate" value={`${data.cleanClaimRatePct}%`} />
            <StatTile label="Denial rate" value={`${data.denialRatePct}%`} tone={data.denialRatePct > 10 ? 'warning' : 'default'} />
            <StatTile label="Collection rate" value={`${data.collectionRatePct}%`} />
            <StatTile label="Days to payment" value={data.daysToPayment ?? '—'} />
            <StatTile label="Days in receivables" value={data.daysInReceivable} />
            <StatTile label="Recovered on resubmission" value={money(data.resubmissionRecoveredMinor)} />
            <StatTile label="Not yet claimed" value={money(data.notYetClaimedMinor)} tone={data.notYetClaimedMinor > 0 ? 'warning' : 'default'} />
          </div>
          <DataTable
            dense
            rows={data.denialsByReason}
            rowKey={(row) => row.reasonFamily}
            empty="No refused lines in this period."
            columns={[
              { header: 'Reason family', sort: (row) => row.reasonFamily, cell: (row) => humanize(row.reasonFamily) },
              { header: 'Lines', align: 'right', sort: (row) => row.lines, cell: (row) => row.lines },
              { header: 'Refused', align: 'right', sort: (row) => row.deniedMinor, cell: (row) => money(row.deniedMinor) },
            ]}
          />
          <p className="text-xs text-muted-foreground">Figures are refreshed at most every five minutes.</p>
        </>
      )}
    </div>
  );
}

interface StatementData {
  openingBalanceMinor: number;
  claims: { id: string; number: string; status: string; submittedAt: string | null; claimedAmountMinor: number; paidAmountMinor: number }[];
  remittances: { id: string; reference: string; paymentDate: string | null; totalMinor: number }[];
  settlements: { id: string; decision: string; amountMinor: number; claimNumber: string }[];
}

export function PayerStatementDialog({ payer, onClose }: { payer: { id: string; name: string }; onClose: () => void }) {
  const toast = useToast();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const statement = useQuery({
    queryKey: ['payer-statement', payer.id, from, to],
    queryFn: () => get<StatementData>(`/insurance/payers/${payer.id}/statement${qs({ from, to, limit: 200 })}`),
  });
  const print = useMutation({
    mutationFn: () => get<{ html: string }>(`/insurance/payers/${payer.id}/panel-bill${qs({ from, to })}`),
    onSuccess: (result) => printDocument(result.html),
    onError: (caught) => toast.error('Could not print', describeError(caught)),
  });
  const data = statement.data;
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={`Statement · ${payer.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="outline" loading={print.isPending} onClick={() => print.mutate()}>
            Print the panel bill
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Field label="From">
            <DatePicker value={from} onChange={(value) => setFrom(value)} />
          </Field>
          <Field label="To">
            <DatePicker value={to} onChange={(value) => setTo(value)} />
          </Field>
        </div>
        {data && (
          <>
            <p className="text-sm">Owed at the start of the period: {money(data.openingBalanceMinor)}</p>
            <DataTable
              dense
              rows={data.claims}
              rowKey={(row) => row.id}
              empty="No claims in this period."
              columns={[
                { header: 'Claim', cell: (row) => row.number },
                { header: 'Sent', cell: (row) => (row.submittedAt ? dateOnly(row.submittedAt) : '—') },
                { header: 'Claimed', align: 'right', cell: (row) => money(row.claimedAmountMinor) },
                { header: 'Paid', align: 'right', cell: (row) => money(row.paidAmountMinor) },
                { header: '', cell: (row) => <StatusBadge status={row.status} /> },
              ]}
            />
            {data.remittances.length > 0 && (
              <DataTable
                dense
                rows={data.remittances}
                rowKey={(row) => row.id}
                columns={[
                  { header: 'Payment', cell: (row) => row.reference },
                  { header: 'Date', cell: (row) => (row.paymentDate ? dateOnly(row.paymentDate) : '—') },
                  { header: 'Amount', align: 'right', cell: (row) => money(row.totalMinor) },
                ]}
              />
            )}
            {data.settlements.length > 0 && (
              <DataTable
                dense
                rows={data.settlements}
                rowKey={(row) => row.id}
                columns={[
                  { header: 'Claim', cell: (row) => row.claimNumber },
                  { header: 'Decision', cell: (row) => humanize(row.decision) },
                  { header: 'Amount', align: 'right', cell: (row) => money(row.amountMinor) },
                ]}
              />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
